<template>
  <div class="relative flex h-screen w-full flex-col items-center overflow-hidden bg-base dark:bg-background-dark">
    <!-- Top Navigation Bar -->
    <header class="flex h-20 w-full flex-shrink-0 items-center justify-center border-b border-content/10 px-4 md:px-10">
      <div class="flex w-full max-w-5xl items-center justify-between">
        <div class="flex items-center gap-4 text-content">
          <h1 class="text-content text-lg font-bold leading-tight tracking-[-0.015em]">{{ $t('login.title') }}</h1>
        </div>
      </div>
    </header>

    <!-- Main Content (이 영역만 스크롤) -->
    <main class="no-scrollbar flex w-full flex-1 flex-col items-center justify-start overflow-y-auto p-4 py-8">
      <div class="w-full max-w-2xl rounded-xl bg-surface dark:bg-surface/50 p-6 md:p-8 border border-line dark:border-roundBorder">

        <!-- Page Heading -->
        <div class="mb-6 text-center">
          <p class="text-content text-3xl md:text-4xl font-black leading-tight tracking-[-0.033em]">{{ $t('account.info.title') }}</p>
          <p class="text-gray-400 dark:text-muted text-base font-normal leading-normal mt-2">{{ $t('account.info.titleSub') }}</p>
        </div>

        <!-- Progress Steps -->
        <div class="flex justify-center gap-2 mb-6">
          <div class="px-4 py-2 rounded-lg bg-content/5 text-muted text-grid-btn">{{ $t('account.common.agree') }}</div>
          <div class="px-4 py-2 rounded-lg bg-primary text-white text-grid-btn font-bold">{{ $t('account.common.info') }}</div>
          <div class="px-4 py-2 rounded-lg bg-content/5 text-muted text-grid-btn">{{ $t('account.common.complete') }}</div>
        </div>

        <!-- Form -->
        <form @submit.prevent="submitForm" autocomplete="off" class="flex flex-col gap-5">

          <!-- kogas 전용: 역할/구분 (빌드 env VUE_APP_TENANT=kogas 일 때만) — 한 행 표시 -->
          <template v-if="isKogas">
            <div class="grid grid-cols-2 gap-5">
              <!-- Role -->
              <div class="min-w-0">
                <label class="text-content text-form-label leading-normal pb-2 block" for="SignRole">
                  역할 <span class="text-red-400">*</span>
                </label>
                <select
                  v-model="SignRole"
                  id="SignRole"
                  disabled
                  class="form-input flex w-full min-w-0 rounded-lg text-content focus:outline-none focus:ring-2 focus:ring-primary border dark:border-roundBorder bg-base focus:border-primary h-12 px-4 text-base font-normal leading-normal disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  <option v-for="r in kogasRoles" :key="r.value" :value="r.value">{{ r.label }}</option>
                </select>
              </div>
              <!-- Type (role=USER 일 때만) -->
              <div v-if="SignRole === 'USER'" class="min-w-0">
                <label class="text-content text-form-label leading-normal pb-2 block" for="SignType">
                  구분 <span class="text-red-400">*</span>
                </label>
                <select
                  v-model="SignType"
                  id="SignType"
                  class="form-input flex w-full min-w-0 rounded-lg text-content focus:outline-none focus:ring-2 focus:ring-primary border dark:border-roundBorder bg-base focus:border-primary h-12 px-4 text-base font-normal leading-normal"
                >
                  <option value="">선택</option>
                  <option v-for="ty in kogasTypes" :key="ty.value" :value="ty.value">{{ ty.label }}</option>
                </select>
              </div>
            </div>
          </template>

          <!-- ID -->
          <div>
            <label class="text-content text-form-label leading-normal pb-2 block" for="UserId">
              {{ $t('account.info.id') }} <span class="text-red-400">*</span>
            </label>
            <div class="flex items-center gap-2">
              <input
                v-model="UserId"
                @keyup="changeId()"
                @keydown.space.prevent
                type="text"
                maxlength="20"
                autocomplete="off"
                class="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-content focus:outline-none focus:ring-2 focus:ring-primary border dark:border-roundBorder bg-base focus:border-primary h-12 placeholder:text-muted px-4 text-base font-normal leading-normal"
                id="UserId"
                :placeholder="$t('account.info.idPlaceholder')"
              />
              <button
                @click.prevent="CheckIdExist"
                id="CheckIdBtn"
                class="flex flex-shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-4 bg-content/5 dark:bg-line text-content text-grid-btn font-bold leading-normal tracking-[0.015em] hover:bg-content/10 dark:hover:bg-surface-3 transition-colors"
                type="button"
              >
                <span class="truncate">{{ $t('account.info.dupllicateCheck') }}</span>
              </button>
            </div>
          </div>

          <!-- Password -->
          <div>
            <label class="text-content text-form-label leading-normal pb-2 block" for="UserPassword">
              {{ $t('account.info.password') }} <span class="text-red-400">*</span>
            </label>
            <div class="relative flex w-full flex-1 items-stretch">
              <input
                v-model="UserPassword"
                @keyup="passwordValid(), passwordCheckValid()"
                :type="passwordVisible ? 'text' : 'password'"
                maxlength="16"
                autocomplete="new-password"
                :class="[
                  'form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-content focus:outline-none focus:ring-2 focus:ring-primary border bg-base h-12 placeholder:text-muted pl-4 pr-12 text-base font-normal leading-normal',
                  passwordValidFlag === false ? 'border-red-500' : 'dark:border-roundBorder focus:border-primary'
                ]"
                id="UserPassword"
                :placeholder="$t('account.info.passwordPlaceholder')"
              />
              <!-- 👁 눈 아이콘 -->
              <button
                type="button"
                @click="passwordVisible = !passwordVisible"
                class="absolute right-0 top-0 flex h-full items-center justify-center pr-4 text-muted hover:text-content focus:outline-none"
              >
                <span class="material-symbols-outlined">
                  {{ passwordVisible ? 'visibility_off' : 'visibility' }}
                </span>
              </button>
            </div>
            <p v-if="passwordValidFlag === false" class="text-red-400 text-xs mt-1">
              {{ $t('account.info.passwordInvalid') }}
            </p>
          </div>

          <!-- Confirm Password -->
          <div>
            <label class="text-content text-form-label leading-normal pb-2 block" for="UserPasswordConfirm">
              {{ $t('account.info.confirmPassword') }} <span class="text-red-400">*</span>
            </label>
            <div class="relative flex w-full flex-1 items-stretch">
              <input
                v-model="UserPasswordConfirm"
                @keyup="passwordCheckValid()"
                :type="passwordConfirmVisible ? 'text' : 'password'"
                maxlength="16"
                autocomplete="new-password"
                :class="[
                  'form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-content focus:outline-none focus:ring-2 focus:ring-primary border bg-base h-12 placeholder:text-muted pl-4 pr-12 text-base font-normal leading-normal',
                  passwordCheckFlag === false && UserPasswordConfirm ? 'border-red-500' : 'dark:border-roundBorder focus:border-primary'
                ]"
                id="UserPasswordConfirm"
                :placeholder="$t('account.info.confirmPasswordPlaceholder')"
              />
              <!-- 👁 눈 아이콘 -->
              <button
                type="button"
                @click="passwordConfirmVisible = !passwordConfirmVisible"
                class="absolute right-0 top-0 flex h-full items-center justify-center pr-4 text-muted hover:text-content focus:outline-none"
              >
                <span class="material-symbols-outlined">
                  {{ passwordConfirmVisible ? 'visibility_off' : 'visibility' }}
                </span>
              </button>
            </div>
            <p v-if="passwordCheckFlag === false && UserPasswordConfirm" class="text-red-400 text-xs mt-1">
              {{ $t('account.info.passwordNotMatch') }}
            </p>
          </div>

          <!-- Glass 사용여부 (Radio: 사용/미사용, 기본=미사용) + PIN -->
          <div>
            <div class="flex items-center justify-between">
              <label class="text-content text-form-label leading-normal block">
                {{ $t('account.info.glassUse') }} <span class="text-red-400">*</span>
              </label>
              <!-- Radio: 사용 / 미사용 -->
              <div class="flex items-center gap-4">
                <label class="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="glassUse"
                    :value="true"
                    v-model="GlassUse"
                    @change="onGlassUseChange"
                    class="glass-radio h-4 w-4 cursor-pointer"
                  />
                  <span class="text-sm text-content">{{ $t('account.info.glassUseYes') }}</span>
                </label>
                <label class="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="glassUse"
                    :value="false"
                    v-model="GlassUse"
                    @change="onGlassUseChange"
                    class="glass-radio h-4 w-4 cursor-pointer"
                  />
                  <span class="text-sm text-content">{{ $t('account.info.glassUseNo') }}</span>
                </label>
              </div>
            </div>

            <!-- PIN PW (Glass 사용 시에만 표시) -->
            <div v-if="GlassUse" class="mt-3">
              <p class="text-content text-form-label leading-normal block mb-1.5">{{ $t('account.info.glassPinDesc') }}</p>
              <div class="relative flex w-full flex-1 items-stretch">
                <input
                  v-model="PinPw"
                  @keyup="pinValid()"
                  @keydown.space.prevent
                  :type="pinVisible ? 'text' : 'password'"
                  inputmode="numeric"
                  maxlength="4"
                  autocomplete="off"
                  :class="[
                    'form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-content focus:outline-none focus:ring-2 focus:ring-primary border bg-base h-12 placeholder:text-muted pl-4 pr-12 text-base font-normal leading-normal tracking-[0.5em]',
                    (pinValidFlag === false && PinPw ? 'border-red-500' : 'dark:border-roundBorder focus:border-primary')
                  ]"
                  id="PinPw"
                  :placeholder="$t('account.info.glassPinPlaceholder')"
                />
                <!-- 👁 눈 아이콘 -->
                <button
                  type="button"
                  @click="pinVisible = !pinVisible"
                  class="absolute right-0 top-0 flex h-full items-center justify-center pr-4 text-muted hover:text-content focus:outline-none"
                >
                  <span class="material-symbols-outlined">
                    {{ pinVisible ? 'visibility_off' : 'visibility' }}
                  </span>
                </button>
              </div>
              <p v-if="pinValidFlag === false && PinPw" class="text-red-400 text-xs mt-1">
                {{ $t('account.info.glassPinInvalid') }}
              </p>
            </div>
          </div>

          <!-- Affiliation -->
          <div>
            <label class="text-content text-form-label leading-normal pb-2 block" for="Company">
              {{ $t('account.info.affiliation') }} <span class="text-red-400">*</span>
            </label>
            <div class="relative">
              <select
                v-model="Company"
                autocomplete="off"
                class="form-select appearance-none w-full rounded-lg text-content focus:outline-none focus:ring-2 focus:ring-primary border dark:border-roundBorder bg-base focus:border-primary h-12 px-4 text-base font-normal leading-normal"
                id="Company"
              >
                <option
                  v-for="company in companyOptions"
                  :key="company.value"
                  :value="company.value"
                >
                  {{ company.text }}
                </option>
              </select>
            </div>
          </div>

          <!-- Name -->
          <div>
            <label class="text-content text-form-label leading-normal pb-2 block" for="UserName">
              {{ $t('account.info.name') }} <span class="text-red-400">*</span>
            </label>
            <input
              v-model="UserName"
              @keydown.space.prevent
              @keyup="nameValid()"
              @input="nameValid()"
              type="text"
              maxlength="10"
              autocomplete="off"
              :class="[
                'form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-content focus:outline-none focus:ring-2 focus:ring-primary border bg-base h-12 placeholder:text-muted px-4 text-base font-normal leading-normal',
                nameValidFlag === false && UserName ? 'border-red-500' : 'dark:border-roundBorder focus:border-primary'
              ]"
              id="UserName"
              :placeholder="$t('account.info.namePlaceholder')"
            />
            <p v-if="nameValidFlag === false && UserName" class="text-red-400 text-xs mt-1">
              {{ $t('account.info.nameInvalid') }}
            </p>
          </div>

          <!-- Phone Number -->
          <div>
            <label class="text-content text-form-label leading-normal pb-2 block" for="Phone">{{ $t('account.info.phone') }}
              <span class="text-red-400">*</span>
            </label>
            <input
              v-model="Phone"
              @keyup="getPhoneValid(Phone)"
              type="text"
              maxlength="13"
              autocomplete="off"
              class="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-content focus:outline-none focus:ring-2 focus:ring-primary border dark:border-roundBorder bg-base focus:border-primary h-12 placeholder:text-muted px-4 text-base font-normal leading-normal"
              id="Phone"
              :placeholder="$t('account.info.phonePlaceholder')"
            />
          </div>

          <!-- Email -->
          <div>
            <label class="text-content text-form-label leading-normal pb-2 block" for="Email">
              {{ $t('account.info.email') }} <span class="text-red-400">*</span>
            </label>
            <div class="flex items-center gap-2">
              <input
                v-model="Email"
                @keyup="emailValidCheck()"
                type="text"
                maxlength="50"
                autocomplete="off"
                :class="[
                  'form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-content focus:outline-none focus:ring-2 focus:ring-primary border bg-base h-12 placeholder:text-muted px-4 text-base font-normal leading-normal',
                  emailValidCheckFlag === false && Email ? 'border-red-500' : 'dark:border-roundBorder focus:border-primary'
                ]"
                id="Email"
                :placeholder="$t('account.info.emailPlaceholder')"
              />
              <button
                @click.prevent="CheckEmailExist"
                id="CheckEmailBtn"
                class="flex flex-shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-4 bg-content/5 dark:bg-line text-content text-grid-btn font-bold leading-normal tracking-[0.015em] hover:bg-content/10 dark:hover:bg-surface-3 transition-colors"
                type="button"
              >
                <span class="truncate">{{ $t('account.info.dupllicateCheck') }}</span>
              </button>
            </div>
            <p v-if="emailValidCheckFlag === false && Email" class="text-red-400 text-xs mt-1">
              {{ $t('account.info.emailInvalid') }}
            </p>
          </div>

          <!-- Email Subscription Checkbox -->
          <div class="flex items-center gap-3 pt-2">
            <input
              v-model="EmailAgree"
              class="app-check h-5 w-5 rounded cursor-pointer"
              id="EmailAgree"
              type="checkbox"
            />
            <label class="text-sm text-gray-300 dark:text-muted" for="EmailAgree">
              {{ $t('account.info.emailAgreeLabel') }}
            </label>
          </div>

          <!-- Sign Up Button -->
          <div class="pt-4">
            <button
              id="submitbtn"
              type="button"
              @click="submitForm"
              :disabled="!isFormValid"
              :class="[
                'flex w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg h-14 px-4 text-grid-btn font-bold leading-normal tracking-[0.015em]',
                isFormValid ? 'bg-primary text-content hover:bg-primary/90' : 'bg-primary/40 text-white/60 cursor-not-allowed'
              ]"
            >
              <span class="truncate">{{ $t('account.info.signUpComplete') }}</span>
            </button>
          </div>
        </form>

        <!-- Footer Link -->
        <div class="mt-6 text-center">
          <p class="text-sm text-gray-400 dark:text-muted">
            {{ $t('account.info.alreadyHaveAccount') }}
            <a @click="$router.push('/login')" class="font-bold text-primary hover:underline cursor-pointer">{{ $t('account.info.logIn') }}</a>
          </p>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios'
import router from '../../router/index'
import { useI18n } from 'vue-i18n';
import { CustomAlert } from '@/assets/js/util.js';
import { ref, watch, onMounted, computed } from 'vue';
import { encryptPassword } from '@/store/modules/loginStore';
import { AUTH_URL } from '@/config';
import { IS_KOGAS, KOGAS_ROLES, KOGAS_TYPES, KOGAS_ROLE_FILTER, KOGAS_TYPE_FILTER, KOGAS_COMPANY_CODE } from '@/config/kogasSignup';

const axiosConfig = {
    headers:{
        "Content-Type": "application/json"
    }
}

export default {
    name: 'SignUp',
    setup() {
      const UserId = ref('');
      const UserPassword = ref('');
      const UserPasswordConfirm = ref('');
      const Company = ref('');
      const UserName = ref('');
      const UserNickName = ref('');
      const ContactNo = ref('');
      const Phone = ref('');
      const SmsAgree = ref(false);
      const NickNameAgree = ref(false);
      const Email = ref('');
      const EmailAgree = ref(false);
      const GlassUse = ref(false);      // Glass 사용 여부 (Toggle)
      const PinPw = ref('');            // Glass 로그인용 PIN (숫자 4자리)

      // kogas 전용: 역할/구분 (빌드 env VUE_APP_TENANT=kogas 일 때만 노출)
      // 전체 값은 상수에 유지하되, 화면엔 필터로 허용된 항목만 노출.
      const isKogas = IS_KOGAS;
      const kogasRoles = KOGAS_ROLES.filter((r) => KOGAS_ROLE_FILTER.includes(r.value));
      const kogasTypes = KOGAS_TYPES.filter((ty) => KOGAS_TYPE_FILTER.includes(ty.value));
      const SignRole = ref('USER');     // → identity_user.role (현재 USER 고정)
      const SignType = ref('');         // → identity_user.type (role=USER 일 때만)

      const { t } = useI18n()

      // 소속 콤보박스 옵션
      const companyOptions = ref([]);

      const idDuplicateFlag = ref(false);
      const idValidFlag = ref(false);
      const nameValidFlag = ref();       // 이름: 영문/한글/숫자 2~10자
      const nickNameValidFlag = ref(false);
      const emailValidCheckFlag = ref();
      const passwordValidFlag = ref();
      const passwordCheckFlag = ref();
      const pinValidFlag = ref();
      const companyVerified = ref(false);

      const passwordVisible = ref(false);
      const passwordConfirmVisible = ref(false);
      const pinVisible = ref(false);

      // Computed property for form validation
      const isFormValid = computed(() => {
        return !!UserId.value && !!UserPassword.value && !!UserPasswordConfirm.value &&
          !!UserName.value && !!Phone.value && !!Email.value && EmailAgree.value == true &&
          nameValidFlag.value == true &&
          idDuplicateFlag.value == true && idValidFlag.value == true && emailValidCheckFlag.value == true &&
          passwordValidFlag.value == true && passwordCheckFlag.value == true && companyVerified.value == true &&
          (!GlassUse.value || pinValidFlag.value === true);   // Glass 사용 시 PIN(4자리) 필수
      });

      watch([UserId, UserPassword,
        UserPasswordConfirm,
        UserName,
        UserNickName,
        ContactNo,
        Phone,
        SmsAgree,
        Email,
        EmailAgree,
        GlassUse,
        PinPw,
        pinValidFlag,
        idDuplicateFlag,
        idValidFlag,
        emailValidCheckFlag,
        passwordCheckFlag,
        passwordValidFlag,
        companyVerified], () => {
          AllCheck();
      });

      // Watch Company change to verify password
      watch(Company, async (newValue, oldValue) => {
        // kogas: 소속=KOGAS 고정, 소속 비밀번호 검증 없이 통과
        if (isKogas) {
          companyVerified.value = !!newValue;
          return;
        }
        if (newValue && newValue !== oldValue) {
          companyVerified.value = false;
          await verifyCompanyPassword();
        } else if (!newValue) {
          companyVerified.value = false;
        }
      });

      async function submitForm() {
        if (!UserId.value || !UserPassword.value || !UserPasswordConfirm.value ||
          !UserName.value || !Phone.value || !Email.value) {
          CustomAlert(t('account.info.missingFields'));
          return;
        }
        // 이름: 영문/한글/숫자 2~10자
        if (!/^[A-Za-z0-9가-힣]{2,10}$/.test(UserName.value)) {
          CustomAlert(t('account.info.nameInvalid'));
          return;
        }
        if (EmailAgree.value == false) {
          CustomAlert(t('account.info.agreeRequired'));
          return;
        }
        // Glass 사용 시 PIN(숫자 4자리) 필수
        if (GlassUse.value && !/^\d{4}$/.test(PinPw.value)) {
          CustomAlert(t('account.info.glassPinRequired'));
          return;
        }
        // kogas: role=USER 이면 구분(type) 필수
        if (isKogas && SignRole.value === 'USER' && !SignType.value) {
          CustomAlert('구분을 선택해 주세요.');
          return;
        }

        try {
          // Auth(8723) 회원가입. 비밀번호는 RSA 암호화(서버가 복호화).
          const encryptedPw = await encryptPassword(UserPassword.value);
          // Glass PIN 도 동일하게 RSA 암호화 전송 (미사용 시 null)
          const encryptedPin = GlassUse.value ? await encryptPassword(PinPw.value) : null;
          const registerData = {
            user_id: UserId.value,
            password: encryptedPw,
            name: UserName.value,
            email: Email.value,
            phone: Phone.value.replace(/[^0-9]/g, ''),
            companyCode: Company.value, // 소속 코드 → identity_user.companyCode
            passwordPin: encryptedPin,  // Glass 로그인용 PIN → identity_user.password_pin
            promotionYn: EmailAgree.value ? 1 : 0, // 마지막 체크박스(수신동의) → identity_user.promotionYn
          };
          // kogas 전용: role/type 전송 (서버가 화이트리스트 검증, ADMIN/SYSTEM 은 type null)
          if (isKogas) {
            registerData.role = SignRole.value;
            registerData.type = SignRole.value === 'USER' ? SignType.value : null;
          }
          const response = await axios.post(`${AUTH_URL}/register`, registerData, axiosConfig);
          if (response.data && response.data.result === 'OK') {
            router.push('/signup/finishjoin');
          } else {
            CustomAlert('회원가입에 실패했습니다.');
          }
        } catch (error) {
          console.log(error);
          const data = error.response?.data;
          const msg =
            (data && typeof data === 'object' ? data.message : data) ||
            '회원가입에 실패했습니다.';
          CustomAlert(msg);
        }
      };

      function CheckIdExist() {
        if (!/^[a-z]+[a-z0-9]{5,19}$/g.test(UserId.value)) {
          CustomAlert( t('account.info.idRequired') );
          UserId.value = '';
          return idValidFlag.value = false;
        }
        else {
          idValidFlag.value = true;
          if ( UserId.value != null){
            axios.get(`${AUTH_URL}/check-id`, {params: {UserId:UserId.value}})
              .then((res) => {
                idDuplicateFlag.value = res.data.available;
                if (idDuplicateFlag.value === true) {
                    document.getElementById("CheckIdBtn").setAttribute("disabled", "true")
                    CustomAlert(t('account.info.idAvailable'));
                } else {
                    document.getElementById("CheckIdBtn").removeAttribute("disabled");
                    CustomAlert(t('account.info.idAlreadyUsed'));
                    UserId.value = '';
                }
              }
            )
            .catch(error => console.log(error));
          }
          else {
            CustomAlert(t('account.info.enterIdFirst'));
            return idValidFlag.value = false;
          }
        }
      };

      function CheckEmailExist(){
        if (!/.+@.+\..+/.test(Email.value)) {
          CustomAlert(t('account.info.emailInvalid'));
          return;
        }
        axios.get(`${AUTH_URL}/check-email`, {params: {Email:Email.value}})
          .then((res) => {
            emailValidCheckFlag.value = res.data.available;
            if (emailValidCheckFlag.value === true) {
              document.getElementById("CheckEmailBtn").setAttribute("disabled", "true")
              CustomAlert(t('account.info.emailAvailable'));
            } else {
              document.getElementById("CheckEmailBtn").removeAttribute("disabled");
              CustomAlert(t('account.info.emailAlreadyUsed'));
              Email.value = '';
            }
          }
        )
        .catch(error => console.log(error));
      }

      function changeId() {
        idValidFlag.value = false;
        idDuplicateFlag.value = false;
        document.getElementById("CheckIdBtn").removeAttribute("disabled");
      };

      function changeUserNickName() {
        nickNameValidFlag.value = false;
        document.getElementById("CheckUserNickNameBtn").removeAttribute("disabled");
      };

      function emailValidCheck() {
        if (/.+@.+\..+/.test(Email.value)) {
          emailValidCheckFlag.value = true;
        } else {
          emailValidCheckFlag.value = false;
        }
      };

      // 이름: 영문 대소문자 + 한글(완성형) + 숫자, 2~10자
      function nameValid() {
        nameValidFlag.value = /^[A-Za-z0-9가-힣]{2,10}$/.test(UserName.value);
      };

      function passwordValid () {
        if (/^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,16}$/.test(UserPassword.value)) {
          passwordValidFlag.value = true;
        } else {
          passwordValidFlag.value = false;
        }

        if (UserPassword.value.search(" ") != -1) {
          CustomAlert(t('account.info.noSpaceAllowed'));
          UserPassword.value = UserPassword.value.replace(/\s/g, "");
        }
      };

      function passwordCheckValid() {
        if(UserPassword.value === UserPasswordConfirm.value) {
          passwordCheckFlag.value = true
        }
        else {
          passwordCheckFlag.value = false
        }

        if (UserPasswordConfirm.value.search(" ") != -1) {
          CustomAlert(t('account.info.noSpaceAllowed'));
          UserPasswordConfirm.value = UserPasswordConfirm.value.replace(/\s/g, "");
        }
      };

      // Glass 사용 여부 라디오 변경 (미사용 선택 시 PIN 초기화)
      function onGlassUseChange() {
        if (!GlassUse.value) {
          PinPw.value = '';
          pinValidFlag.value = undefined;
          pinVisible.value = false;
        }
      };

      // Glass PIN 검증 (숫자만 허용, 정확히 4자리)
      function pinValid() {
        PinPw.value = PinPw.value.replace(/[^0-9]/g, '');
        pinValidFlag.value = /^\d{4}$/.test(PinPw.value);
      };

      function getPhoneValid(val) {
        let res = phoneValid(val);
        Phone.value = res
      };

      function phoneValid(phoneNumber) {
        if(!phoneNumber) {
            return phoneNumber;
        }
        phoneNumber = phoneNumber.replace(/[^0-9]/g, '')

        let res = ''
        if(phoneNumber.length < 3) {
            res = phoneNumber
        }
        else {
          if(phoneNumber.substr(0, 2) =='02') {
            if(phoneNumber.length <= 5) {//02-123-5678
              res = phoneNumber.substr(0, 2) + '-' + phoneNumber.substr(2, 3)
            }
            else if(phoneNumber.length > 5 && phoneNumber.length <= 9) {//02-123-5678
              res = phoneNumber.substr(0, 2) + '-' + phoneNumber.substr(2, 3) + '-' + phoneNumber.substr(5)
            }
            else if(phoneNumber.length > 9) {//02-1234-5678
              res = phoneNumber.substr(0, 2) + '-' + phoneNumber.substr(2, 4) + '-' + phoneNumber.substr(6)
            }
          }
          else {
            if(phoneNumber.length < 8) {
              res = phoneNumber
            }
            else if(phoneNumber.length == 8)
            {
              res = phoneNumber.substr(0, 4) + '-' + phoneNumber.substr(4)
            }
            else if(phoneNumber.length == 9)
            {
              res = phoneNumber.substr(0, 3) + '-' + phoneNumber.substr(3, 3) + '-' + phoneNumber.substr(6)
            }
            else if(phoneNumber.length == 10)
            {
              res = phoneNumber.substr(0, 3) + '-' + phoneNumber.substr(3, 3) + '-' + phoneNumber.substr(6)
            }
            else if(phoneNumber.length > 10) { //010-1234-5678
              res = phoneNumber.substr(0, 3) + '-' + phoneNumber.substr(3, 4) + '-' + phoneNumber.substr(7)
            }
          }
        }
        return res
      };

      function AllCheck() {
        if(
          !!UserId.value && !!UserPassword.value && !!UserPasswordConfirm.value &&
          !!UserName.value && !!Phone.value && !!Email.value && EmailAgree.value == true &&
          nameValidFlag.value == true &&
          idDuplicateFlag.value == true && idValidFlag.value == true && emailValidCheckFlag.value == true &&
          passwordValidFlag.value == true && passwordCheckFlag.value == true && companyVerified.value == true &&
          (!GlassUse.value || pinValidFlag.value === true) ){
            document.getElementById('submitbtn').removeAttribute("disabled");
            return true;
          }
          else{
              document.getElementById('submitbtn').setAttribute("disabled", "true");
              return false;
          }
      };

      // Verify Company Password
      async function verifyCompanyPassword() {
        if (!Company.value) return;

        const { value: password } = await Swal.fire({
          title: t('account.info.affiliationVerify'),
          text: t('account.info.affiliationVerifyDesc'),
          input: 'password',
          inputPlaceholder: t('account.info.password'),
          theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
          showCancelButton: false,
          confirmButtonText: t('account.info.confirm'),
          confirmButtonColor: "#3085d6",
        });

        if (!password) {
          Company.value = '';
          companyVerified.value = false;
          return;
        }

        try {
          const response = await axios.post('/api/users/verifyCompany', {
            companyCode: Company.value,
            password: password
          });

          if (response.data.valid) {
            companyVerified.value = true;
            Swal.fire({
              title: t('account.info.verified'),
              text: t('account.info.affiliationVerified'),
              theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
              confirmButtonColor: "#3085d6",
            });
          } else {
            companyVerified.value = false;
            Company.value = '';
            Swal.fire({
              title: t('account.info.verifyFailed'),
              text: t('account.info.affiliationVerifyFailed'),
              theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
              confirmButtonColor: "#3085d6",
            });
          }
        } catch (error) {
          console.error('Failed to verify company password:', error);
          companyVerified.value = false;
          Company.value = '';
          Swal.fire({
            title: t('account.info.error'),
            text: t('account.info.affiliationVerifyError'),
            icon: "error",
            theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
            confirmButtonColor: "#3085d6",
          });
        }
      }

      // Fetch company list from API
      async function fetchCompanyList() {
        try {
          const response = await axios.get('/api/users/companies');
          if (response.data && Array.isArray(response.data)) {
            // kogas 빌드면 소속을 KOGAS 하나로 제한
            const list = isKogas
              ? response.data.filter(c => c.CompanyCode === KOGAS_COMPANY_CODE)
              : response.data;
            companyOptions.value = [
              { value: '', text: 'Select your affiliation' },
              ...list.map(company => ({
                value: company.CompanyCode,
                text: company.CompanyName
              }))
            ];
          }
        } catch (error) {
          console.error('Failed to fetch company list:', error);
        }
      }

      // Load company list on component mount
      onMounted(() => {
        fetchCompanyList();
        // kogas: 소속 기본값 KOGAS + 소속 검증 통과(true)
        if (isKogas) {
          Company.value = KOGAS_COMPANY_CODE;
          companyVerified.value = true;
        }
      });

      return {
        UserId,
        UserPassword,
        UserPasswordConfirm,
        Company,
        companyOptions,
        UserName,
        UserNickName,
        NickNameAgree,
        ContactNo,
        Phone,
        SmsAgree,
        Email,
        EmailAgree,
        GlassUse,
        PinPw,

        isKogas,
        kogasRoles,
        kogasTypes,
        SignRole,
        SignType,

        idDuplicateFlag,
        idValidFlag,
        nameValidFlag,
        nickNameValidFlag,
        emailValidCheckFlag,
        passwordValidFlag,
        passwordCheckFlag,
        pinValidFlag,
        passwordVisible,
        passwordConfirmVisible,
        pinVisible,
        companyVerified,
        isFormValid,

        submitForm,
        CheckIdExist,
        CheckEmailExist,

        changeId,
        changeUserNickName,
        nameValid,
        emailValidCheck,
        passwordValid,
        passwordCheckValid,
        onGlassUseChange,
        pinValid,
        getPhoneValid,
        phoneValid,
        AllCheck,
      };
    }
}
</script>

<style scoped>
  /* 회원가입 체크박스: 전역 _inputs.scss 의 input[type=checkbox]::before(체크 시 인디고 #3f51b5 채움) 제거 —
     색 채움 없이 테두리 박스 + 파란 체크마크만 유지 (로그인 아이디저장과 동일 처리) */
  .app-check {
    appearance: none !important;
    -webkit-appearance: none !important;
    width: 1.25rem !important;
    height: 1.25rem !important;
    border: 1.5px solid rgb(var(--c-muted)) !important;
    border-radius: 4px !important;
    background-color: transparent !important;
    background-image: none !important;
    cursor: pointer;
  }
  .app-check:checked {
    border-color: #137fec !important;
    background-color: transparent !important;
    background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M3 8l3.5 3.5L13 5' stroke='%23137fec' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e") !important;
    background-size: 88% 88% !important;
    background-position: center !important;
    background-repeat: no-repeat !important;
  }
  .app-check:focus,
  .app-check:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }
  .app-check::before {
    display: none !important;
    content: none !important;
  }

  /* PIN 입력값은 넓은 letter-spacing(tracking) 을 쓰지만, placeholder 는 기본 간격으로 */
  #PinPw::placeholder {
    letter-spacing: normal;
  }

  /* Glass 사용여부 라디오: forms/Bootstrap/네이티브가 체크 시 원(또는 블록) 전체를 색으로 채우는 것을 방지 →
     테두리 원 + 중앙 작은 점(radial-gradient)만 표시. ::before(전역 체크박스 채움) 도 제거. */
  .glass-radio {
    appearance: none !important;
    -webkit-appearance: none !important;
    width: 1rem !important;
    height: 1rem !important;
    border: 1.5px solid rgb(var(--c-muted)) !important;
    border-radius: 9999px !important;
    background-color: transparent !important;
    background-image: none !important;
    cursor: pointer;
  }
  .glass-radio:checked {
    border-color: #137fec !important;
    background-color: transparent !important;
    /* 중앙 점만 — 원 전체가 아니라 45% 반경까지만 파랑, 나머지 투명 */
    background-image: radial-gradient(circle, #137fec 0 42%, transparent 46%) !important;
  }
  .glass-radio:focus,
  .glass-radio:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }
  .glass-radio::before {
    display: none !important;
    content: none !important;
  }

  /* 스크롤 기능은 유지하고 스크롤바만 숨김 */
  .no-scrollbar {
    -ms-overflow-style: none;   /* IE, 구 Edge */
    scrollbar-width: none;      /* Firefox */
  }
  .no-scrollbar::-webkit-scrollbar {
    display: none;              /* Chrome, Edge, Safari */
  }

  /* Chrome / Edge */
  input[type="password"]::-ms-reveal,
  input[type="password"]::-ms-clear {
    display: none;
  }

  /* WebKit (Chrome, Edge, Safari 일부) */
  input[type="password"]::-webkit-credentials-auto-fill-button,
  input[type="password"]::-webkit-contacts-auto-fill-button {
    visibility: hidden;
    display: none !important;
  }

</style>